import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, CheckCircle, XCircle, Clock, AlertTriangle, DollarSign, FileText,
  RefreshCw, Loader2, Lock, Unlock, Activity, Radio, Play, PlayCircle,
  MessageCircle, TrendingUp, Heart, Megaphone, Server
} from "lucide-react";
import { EmergentAid } from "../components/EmergentAid";
import { QualityMetricsPanel } from "../components/QualityMetricsPanel";
import { FeedbackInbox } from "../components/FeedbackInbox";
import { StatsPanel } from "../components/StatsPanel";
import { EmailQueuePanel } from "../components/EmailQueuePanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Beaker = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4.5 3h15"/><path d="M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3"/><path d="M6 14h12"/>
  </svg>
);

// ============================================================
// APPROVAL CARD
// ============================================================
const ApprovalCard = ({ item, type, onApprove }) => {
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(null);

  const handleAction = async (action) => {
    setProcessing(action);
    try {
      await onApprove(type === "decision" ? item.decision_id : item.transaction_id, action, notes);
      toast.success(`${action === "approve" ? "Goedgekeurd" : "Afgewezen"}`);
    } catch { toast.error("Actie mislukt"); }
    finally { setProcessing(null); }
  };

  return (
    <div className="bg-white border-2 border-gray-200 p-5" data-testid={`approval-${type}-${item.decision_id || item.transaction_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 ${item.risk_level === "high" || item.risk_level === "critical" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
            {item.risk_level || item.status || "PENDING"}
          </span>
          <span className="text-[10px] font-mono text-gray-400 ml-2">{item.category || item.flow_type}</span>
        </div>
        <span className="text-[10px] font-mono text-gray-400">{item.initiating_agent || item.initiated_by}</span>
      </div>
      <p className="text-sm font-medium mb-2">{item.final_decision || item.description || "Pending approval"}</p>
      {item.financial_impact > 0 && <p className="text-sm font-bold text-red-600 mb-2">EUR {item.financial_impact || item.amount}</p>}
      {item.reasoning && <p className="text-xs text-gray-500 mb-3">{item.reasoning.substring(0, 200)}</p>}
      <div className="flex gap-2 items-end">
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Opmerkingen..."
          className="flex-1 text-xs border border-gray-200 px-2 py-1.5 focus:outline-none focus:border-[#002FA7]" />
        <button onClick={() => handleAction("approve")} disabled={!!processing}
          className="bg-green-600 text-white px-3 py-1.5 text-xs font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
          data-testid={`approve-btn-${item.decision_id || item.transaction_id}`}
        >
          {processing === "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} GOEDKEUREN
        </button>
        <button onClick={() => handleAction("reject")} disabled={!!processing}
          className="bg-red-600 text-white px-3 py-1.5 text-xs font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
          data-testid={`reject-btn-${item.decision_id || item.transaction_id}`}
        >
          {processing === "reject" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />} AFWIJZEN
        </button>
      </div>
    </div>
  );
};

// ============================================================
// LIVE ACTIVITY FEED
// ============================================================
const EVENT_ICONS = { decision: FileText, alert: AlertTriangle, cycle: Activity, transaction: DollarSign, review: MessageCircle };
const AGENT_COLORS = { CEO_001: "#002FA7", CFO_001: "#002FA7", COMP_001: "#FF2A2A", OPS_001: "#00805A", CS_001: "#B8860B", MKT_001: "#7000FF" };
const STATUS_STYLES = {
  executed: "bg-green-100 text-green-700", blocked: "bg-red-100 text-red-700", pending_approval: "bg-yellow-100 text-yellow-700",
  active: "bg-orange-100 text-orange-700", resolved: "bg-green-100 text-green-700", completed: "bg-blue-100 text-blue-700",
  logged: "bg-gray-100 text-gray-600", pending: "bg-yellow-100 text-yellow-700", unknown: "bg-gray-100 text-gray-500",
};
const SEVERITY_BG = { critical: "bg-red-50 border-l-red-400", high: "bg-orange-50 border-l-orange-300", normal: "bg-gray-50 border-l-gray-200", medium: "bg-yellow-50 border-l-yellow-300" };

const ActivityEvent = ({ event }) => {
  const Icon = EVENT_ICONS[event.event_type] || Activity;
  const agentColor = AGENT_COLORS[event.agent] || "#4A4D57";
  const bg = SEVERITY_BG[event.severity] || SEVERITY_BG.normal;
  const statusCls = STATUS_STYLES[event.status] || STATUS_STYLES.unknown;

  return (
    <div className={`flex gap-3 py-3 px-4 border-l-[3px] ${bg}`} data-testid="activity-event">
      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: agentColor }}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 text-white" style={{ backgroundColor: agentColor }}>{event.agent}</span>
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${statusCls}`}>{event.status}</span>
          {event.category && <span className="text-[10px] font-mono text-gray-400 uppercase">{event.category}</span>}
        </div>
        <p className="text-sm mt-1 leading-snug text-gray-800 truncate">{event.title}</p>
        <p className="text-[10px] font-mono text-gray-400 mt-1">{event.timestamp ? new Date(event.timestamp).toLocaleString() : ""}</p>
      </div>
    </div>
  );
};

const LiveActivityFeed = ({ events, loading, onRefresh }) => (
  <div className="bg-white border-2 border-gray-200" data-testid="activity-feed">
    <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-900 text-white">
      <div className="flex items-center gap-2">
        <Radio className="w-4 h-4 text-green-400 animate-pulse" />
        <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>LIVE AGENT ACTIVITY</h3>
        <span className="text-[10px] font-mono text-gray-400">{events.length} events</span>
      </div>
      <button onClick={onRefresh} className="hover:bg-gray-800 p-1"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
    </div>
    <div className="max-h-[600px] overflow-y-auto divide-y divide-gray-100">
      {events.length > 0 ? events.map((e, i) => <ActivityEvent key={`${e.event_type}-${e.timestamp}-${i}`} event={e} />) : (
        <div className="p-10 text-center text-gray-400"><Activity className="w-10 h-10 mx-auto mb-3 text-gray-300" /><p className="font-mono text-sm">No agent activity yet</p></div>
      )}
    </div>
  </div>
);

// ============================================================
// SIMULATION PANEL
// ============================================================
const SCENARIO_ICONS = { budget_overrun: DollarSign, compliance_conflict: Shield, marketing_campaign: Megaphone, satisfaction_drop: Heart, capacity_alert: Server, revenue_milestone: TrendingUp };
const SEV_STYLES = { critical: "border-red-400 bg-red-50", high: "border-orange-300 bg-orange-50", normal: "border-green-300 bg-green-50" };

const SCENARIO_SEVERITY_BADGE = {
  critical: "bg-red-200 text-red-800",
  high: "bg-orange-200 text-orange-800",
};
const DEFAULT_SCENARIO_BADGE = "bg-green-200 text-green-800";

const getFinancialModeLabel = (isActive, systemMode) => {
  if (!isActive) return "SAFE MODE";
  return systemMode === "no_spend" ? "NO-SPEND MODE" : "GROWTH BUDGET";
};

const SimulationPanel = ({ onScenarioComplete }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/simulation/scenarios`).then(r => setScenarios(r.data.scenarios || [])).catch((err) => {
      if (process.env.NODE_ENV !== "production") console.debug("[Scenarios] load failed:", err?.message);
    }).finally(() => setLoading(false));
  }, []);

  const runScenario = async (id) => {
    setRunningId(id); setLastResult(null);
    try {
      const res = await axios.post(`${API}/simulation/run`, { scenario_id: id });
      setLastResult(res.data); toast.success(`Scenario "${res.data.scenario?.name}" uitgevoerd`);
      if (onScenarioComplete) onScenarioComplete();
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); setLastResult({ error: e.response?.data?.detail }); }
    finally { setRunningId(null); }
  };

  const runAll = async () => {
    setRunningAll(true); setLastResult(null);
    try {
      const res = await axios.post(`${API}/simulation/run-all`);
      const r = res.data.results || {};
      const total = Object.keys(r).length;
      const approvals = Object.values(r).filter(x => x.awaiting_founder_approval).length;
      toast.success(`${total} scenario's uitgevoerd. ${approvals} wachten op goedkeuring.`);
      setLastResult({ all: true, total, approvals });
      if (onScenarioComplete) onScenarioComplete();
    } catch { toast.error("Mislukt"); }
    finally { setRunningAll(false); }
  };

  if (loading) return <div className="bg-white border-2 border-gray-200 p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;

  return (
    <div className="bg-white border-2 border-gray-200" data-testid="simulation-panel">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-indigo-950 text-white">
        <div className="flex items-center gap-2">
          <Beaker className="w-4 h-4 text-indigo-300" />
          <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>SIMULATION MODE</h3>
          <span className="text-[10px] font-mono text-indigo-300">{scenarios.length} scenarios</span>
        </div>
        <button onClick={runAll} disabled={runningAll || runningId}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold disabled:opacity-50"
          data-testid="run-all-scenarios-btn"
        >
          {runningAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />} RUN ALLE
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
        {scenarios.map(s => {
          const Icon = SCENARIO_ICONS[s.id] || Activity;
          const sev = SEV_STYLES[s.severity] || SEV_STYLES.normal;
          return (
            <div key={s.id} className={`border-2 p-4 ${sev} ${runningId === s.id ? 'ring-2 ring-indigo-400' : ''}`} data-testid={`scenario-card-${s.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-gray-900 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-white" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.name}</p>
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{s.description}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${SCENARIO_SEVERITY_BADGE[s.severity] || DEFAULT_SCENARIO_BADGE}`}>{s.severity}</span>
                    {s.requires_founder_approval && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-yellow-200 text-yellow-800">APPROVAL NODIG</span>}
                  </div>
                </div>
              </div>
              <button onClick={() => runScenario(s.id)} disabled={runningId === s.id || runningAll}
                className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 disabled:opacity-50"
                data-testid={`run-scenario-${s.id}`}
              >
                {runningId === s.id ? <><Loader2 className="w-3 h-3 animate-spin" /> BEZIG...</> : <><Play className="w-3 h-3" /> UITVOEREN</>}
              </button>
            </div>
          );
        })}
      </div>
      {lastResult && !lastResult.error && (
        <div className="px-4 pb-4"><div className="bg-green-50 border-2 border-green-300 p-3">
          <p className="text-sm font-bold text-green-800">{lastResult.all ? `${lastResult.total} scenario's uitgevoerd. ${lastResult.approvals} wachten op goedkeuring.` : lastResult.summary}</p>
          {lastResult.decision_id && <p className="text-xs font-mono text-green-600 mt-1">Decision: {lastResult.decision_id}</p>}
        </div></div>
      )}
      {lastResult?.error && <div className="px-4 pb-4"><div className="bg-red-50 border-2 border-red-300 p-3"><p className="text-sm text-red-700">{lastResult.error}</p></div></div>}
    </div>
  );
};

// ============================================================
// LIVE FINANCIAL DASHBOARD (MANDATORY)
// ============================================================
const FinancialDashboard = () => {
  const [data, setData] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stopReason, setStopReason] = useState("");
  const [showStop, setShowStop] = useState(false);

  const fetchFinancial = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        axios.get(`${API}/financial/dashboard`),
        axios.get(`${API}/financial/proposals?status=pending`)
      ]);
      setData(d.data);
      setProposals(p.data.proposals || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[Financial] fetch failed:", err?.message);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchFinancial(); const i = setInterval(fetchFinancial, 15000); return () => clearInterval(i); }, [fetchFinancial]);

  const handleEmergencyStop = async () => {
    if (!stopReason.trim()) return;
    try {
      await axios.post(`${API}/financial/emergency-stop`, { reason: stopReason });
      toast.error("EMERGENCY STOP geactiveerd");
      setShowStop(false);
      setStopReason("");
      fetchFinancial();
    } catch { toast.error("Emergency stop mislukt"); }
  };

  const handleRestart = async () => {
    try {
      await axios.post(`${API}/financial/restart`, { notes: "Founder restart" });
      toast.success("Systeem herstart");
      fetchFinancial();
    } catch { toast.error("Herstart mislukt"); }
  };

  const handleProposalAction = async (id, action) => {
    try {
      await axios.post(`${API}/financial/proposals/${id}/${action}`, { notes: "" });
      toast.success(action === "approve" ? "Goedgekeurd" : "Afgewezen");
      fetchFinancial();
    } catch { toast.error("Actie mislukt"); }
  };

  if (loading) return null;

  const s = data?.summary || {};
  const isActive = data?.system_active !== false;

  return (
    <div data-testid="financial-dashboard">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
        <DollarSign className="w-5 h-5 text-green-600" />Financial Control
        <span className={`text-[10px] font-mono px-2 py-0.5 ml-2 ${isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {getFinancialModeLabel(isActive, data?.system_mode)}
        </span>
      </h2>

      {!isActive && (
        <div className="bg-red-50 border-2 border-red-400 p-4 mb-4">
          <p className="text-sm font-bold text-red-800">SYSTEEM IN SAFE MODE — Alle agent activiteit gepauzeerd</p>
          <button onClick={handleRestart} className="mt-2 px-3 py-1.5 bg-green-700 text-white text-xs font-bold hover:bg-green-600" data-testid="restart-system-btn">
            HERSTART SYSTEEM
          </button>
        </div>
      )}

      <div className="bg-white border-2 border-gray-200 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-gray-200">
          {[
            ["Omzet", `EUR ${s.total_revenue_received?.toFixed(2) || "0.00"}`, "text-green-700"],
            ["Kosten", `EUR ${s.total_costs_paid?.toFixed(2) || "0.00"}`, "text-red-600"],
            ["Winst", `EUR ${s.net_profit?.toFixed(2) || "0.00"}`, s.net_profit > 0 ? "text-green-700" : "text-gray-600"],
            ["Groei (10%)", `EUR ${s.growth_budget_10pct?.toFixed(2) || "0.00"}`, "text-blue-600"],
            ["Locked (70%)", `EUR ${s.locked_capital_70pct?.toFixed(2) || "0.00"}`, "text-gray-500"],
            ["Reserve (20%)", `EUR ${s.reserve_20pct?.toFixed(2) || "0.00"}`, "text-yellow-600"],
          ].map(([label, value, color]) => (
            <div key={label} className="p-3 text-center">
              <p className="text-[10px] font-mono text-gray-500 uppercase">{label}</p>
              <p className={`text-sm font-black ${color}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pending Proposals */}
      {proposals.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-mono text-gray-500 uppercase mb-2">Openstaande Proposals ({proposals.length})</p>
          <div className="space-y-2">
            {proposals.map(p => (
              <div key={p.proposal_id} className="bg-white border-2 border-yellow-200 p-3" data-testid={`proposal-${p.proposal_id}`}>
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <span className="text-[10px] font-mono bg-yellow-100 text-yellow-700 px-1.5 py-0.5">{p.urgency}</span>
                    <span className="text-[10px] font-mono text-gray-400 ml-2">{p.agent_id}</span>
                  </div>
                  {p.cost_estimate > 0 && <span className="text-xs font-bold text-red-600">EUR {p.cost_estimate?.toFixed(2)}</span>}
                </div>
                <p className="text-sm font-medium">{p.title}</p>
                <p className="text-xs text-gray-500 mt-1">{p.description?.substring(0, 150)}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => handleProposalAction(p.proposal_id, "approve")} className="px-2 py-1 bg-green-600 text-white text-[10px] font-bold hover:bg-green-700" data-testid={`approve-proposal-${p.proposal_id}`}>GOEDKEUREN</button>
                  <button onClick={() => handleProposalAction(p.proposal_id, "reject")} className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold hover:bg-red-700" data-testid={`reject-proposal-${p.proposal_id}`}>AFWIJZEN</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Emergency Stop */}
      <div className="flex gap-2">
        {isActive && (
          showStop ? (
            <div className="flex gap-2 items-center flex-1">
              <input value={stopReason} onChange={e => setStopReason(e.target.value)} placeholder="Reden voor emergency stop..." className="flex-1 text-xs border border-red-300 px-2 py-1.5" />
              <button onClick={handleEmergencyStop} className="px-3 py-1.5 bg-red-700 text-white text-[10px] font-bold" data-testid="confirm-emergency-stop">BEVESTIG STOP</button>
              <button onClick={() => setShowStop(false)} className="px-3 py-1.5 bg-gray-200 text-[10px] font-bold">ANNULEER</button>
            </div>
          ) : (
            <button onClick={() => setShowStop(true)} className="px-3 py-1.5 bg-red-100 text-red-700 text-[10px] font-mono font-bold hover:bg-red-200 border border-red-200" data-testid="emergency-stop-btn">
              EMERGENCY STOP
            </button>
          )
        )}
      </div>
    </div>
  );
};

// ============================================================
// V1 LAUNCH REPORT
// ============================================================
const V1LaunchReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/executive/v1-report`);
      setReport(res.data);
      setExpanded(true);
    } catch { toast.error("Rapport genereren mislukt"); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-600";
    const s = status.toUpperCase();
    if (s === "YES") return "bg-green-100 text-green-700";
    if (s === "NO") return "bg-red-100 text-red-700";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div data-testid="v1-launch-report">
      <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
        <Activity className="w-5 h-5 text-green-600" />V1 Launch Readiness
      </h2>
      {!report ? (
        <div className="bg-white border-2 border-gray-200 p-6 text-center">
          <p className="text-sm text-gray-600 mb-4">Genereer een launch-rapport door alle AI-agents te raadplegen over de V1 gereedheid.</p>
          <button onClick={generateReport} disabled={loading}
            className="px-4 py-2 bg-green-700 text-white font-bold text-sm hover:bg-green-600 disabled:opacity-50 flex items-center gap-2 mx-auto"
            data-testid="generate-v1-report-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
            {loading ? "Agents analyseren..." : "Genereer V1 Rapport"}
          </button>
        </div>
      ) : (
        <div className="bg-white border-2 border-gray-200">
          <div className="p-4 border-b border-gray-200 bg-gray-900 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-400" />
              <span className="font-bold text-sm">V1 LAUNCH ASSESSMENT</span>
            </div>
            <span className="text-[10px] font-mono text-gray-400">{report.generated_at ? new Date(report.generated_at).toLocaleString() : ""}</span>
          </div>
          <div className="p-4 space-y-4">
            {/* Metrics Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center p-2 bg-gray-50"><p className="text-lg font-black">{report.platform_metrics?.debaters_available}</p><p className="text-[10px] font-mono text-gray-500">DEBATERS</p></div>
              <div className="text-center p-2 bg-gray-50"><p className="text-lg font-black">{report.platform_metrics?.groups_available}</p><p className="text-[10px] font-mono text-gray-500">TEAMS</p></div>
              <div className="text-center p-2 bg-gray-50"><p className="text-lg font-black">{report.platform_metrics?.agents_active}</p><p className="text-[10px] font-mono text-gray-500">AGENTS</p></div>
              <div className="text-center p-2 bg-gray-50"><p className="text-lg font-black">{report.platform_metrics?.total_discussions}</p><p className="text-[10px] font-mono text-gray-500">DISCUSSIONS</p></div>
            </div>
            
            {/* Agent Assessments */}
            {Object.entries(report.assessments || {}).map(([role, data]) => {
              const decision = data.go_live_decision || data.financial_readiness || data.compliance_ready || data.technical_ready || "UNKNOWN";
              return (
                <div key={role} className="border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm uppercase">{role}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 font-bold ${getStatusColor(decision)}`}>{decision}</span>
                  </div>
                  <p className="text-xs text-gray-600">{data.reasoning}</p>
                  {data.strengths && <div className="mt-2">{data.strengths.slice(0, 2).map((s) => <p key={`strength-${role}-${s}`} className="text-[11px] text-green-700">+ {s}</p>)}</div>}
                  {data.risks && <div className="mt-1">{data.risks.slice(0, 2).map((r) => <p key={`risk-${role}-${r}`} className="text-[11px] text-red-600">- {r}</p>)}</div>}
                  {data.blockers && data.blockers.length > 0 && <div className="mt-1">{data.blockers.map((b) => <p key={`blocker-${role}-${b}`} className="text-[11px] font-bold text-red-700">BLOCKER: {b}</p>)}</div>}
                </div>
              );
            })}

            <button onClick={generateReport} disabled={loading}
              className="w-full py-2 text-xs font-mono text-gray-500 hover:text-[#002FA7] flex items-center justify-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Opnieuw genereren
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// FOUNDER DASHBOARD
// ============================================================
export const FounderDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [pendingDecisions, setPendingDecisions] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [auditReport, setAuditReport] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [d, t, n, a, f] = await Promise.all([
        axios.get(`${API}/decisions/pending-approval`), axios.get(`${API}/transactions/pending-approval`),
        axios.get(`${API}/founder/notifications?limit=20`), axios.get(`${API}/audit/report?days=30`),
        axios.get(`${API}/agents/activity-feed?limit=40`)
      ]);
      setPendingDecisions(d.data.decisions || []); setPendingTransactions(t.data.transactions || []);
      setNotifications(n.data.notifications || []); setAuditReport(a.data); setActivityFeed(f.data.events || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[FounderDashboard] fetch failed:", err?.message);
    }
    finally { setLoading(false); }
  }, []);

  const refreshFeed = useCallback(async () => {
    setFeedLoading(true);
    try { const r = await axios.get(`${API}/agents/activity-feed?limit=40`); setActivityFeed(r.data.events || []); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[ActivityFeed] refresh failed:", err?.message);
    }
    finally { setFeedLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);

  const handleDecisionApproval = async (id, action, notes) => {
    await axios.post(`${API}/agents/decisions/${id}/${action}`, { notes });
    fetchData();
  };

  const handleTransactionApproval = async (id, action, notes) => {
    await axios.post(`${API}/transactions/${id}/${action}`, { notes });
    fetchData();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="founder-dashboard">
      <div className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>FOUNDER DASHBOARD</h1>
            <p className="text-sm text-gray-400 mt-1">An RBG-LLM project — No Log = No Execution</p>
          </div>
          <div className="flex items-center gap-3">
            {pendingDecisions.length > 0 && <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-1">{pendingDecisions.length} PENDING</span>}
            <button onClick={fetchData} className="p-2 hover:bg-gray-800" data-testid="refresh-founder-btn"><RefreshCw className="w-5 h-5" /></button>
          </div>
        </div>
      </div>

      {/* Alert bar */}
      {(pendingDecisions.length > 0 || pendingTransactions.length > 0) && (
        <div className="bg-yellow-100 border-b-2 border-yellow-300 py-3">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-700" />
            <p className="text-sm text-yellow-800 font-medium">
              {pendingDecisions.length} beslissingen en {pendingTransactions.length} transacties wachten op goedkeuring
            </p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Financial Control Dashboard */}
            <FinancialDashboard />

            {/* Pending Decisions */}
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Lock className="w-5 h-5 text-yellow-600" />Critical Decisions ({pendingDecisions.length})</h2>
              {pendingDecisions.length > 0 ? (
                <div className="space-y-3">{pendingDecisions.map(d => <ApprovalCard key={d.decision_id} item={d} type="decision" onApprove={handleDecisionApproval} />)}</div>
              ) : <div className="bg-white border-2 border-gray-200 p-8 text-center"><Unlock className="w-10 h-10 text-green-500 mx-auto mb-3" /><p className="text-gray-500">Geen beslissingen pending</p></div>}
            </div>

            {/* Pending Transactions */}
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><DollarSign className="w-5 h-5 text-yellow-600" />Outgoing Payments ({pendingTransactions.length})</h2>
              {pendingTransactions.length > 0 ? (
                <div className="space-y-3">{pendingTransactions.map(t => <ApprovalCard key={t.transaction_id} item={t} type="transaction" onApprove={handleTransactionApproval} />)}</div>
              ) : <div className="bg-white border-2 border-gray-200 p-8 text-center"><CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" /><p className="text-gray-500">Geen betalingen pending</p></div>}
            </div>

            {/* Live Feed */}
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Radio className="w-5 h-5 text-green-500 animate-pulse" />Mission Control — Live Feed</h2>
              <LiveActivityFeed events={activityFeed} loading={feedLoading} onRefresh={refreshFeed} />
            </div>

            {/* Simulation */}
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Beaker className="w-5 h-5 text-indigo-500" />Simulatie Modus</h2>
              <SimulationPanel onScenarioComplete={fetchData} />
            </div>

            {/* V1 Launch Report */}
            <V1LaunchReport />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats — server-side, privacy-first */}
            <StatsPanel />

            {/* Inbox — user proposals & reviews */}
            <FeedbackInbox />

            {/* Email queue — DB-only, Stage-2 SMTP worker pending */}
            <EmailQueuePanel />

            {/* Emergent Aid — Founder Assistant */}
            <EmergentAid />

            {/* Quality Metrics — Sycophancy + Disagreement tracking */}
            <QualityMetricsPanel />

            {/* Audit */}
            {auditReport && (
              <div>
                <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Shield className="w-5 h-5" />30-Day Audit</h2>
                <div className="bg-white border-2 border-gray-200 p-5 space-y-3">
                  {[
                    ["Total Decisions", auditReport.total_decisions],
                    ["Critical", auditReport.critical_decisions],
                    ["Vetoed", auditReport.vetoed_decisions],
                    ["Pending Approval", auditReport.pending_approval],
                    ["Approved", auditReport.founder_approved],
                    ["Rejected", auditReport.founder_rejected],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm"><span className="text-gray-600">{k}</span><span className="font-bold font-mono">{v}</span></div>
                  ))}
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Compliance Rate</span><span className="font-bold font-mono text-green-600">{((auditReport.compliance_rate || 0) * 100).toFixed(0)}%</span></div>
                  </div>
                </div>
              </div>
            )}

            {/* Rules */}
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><Lock className="w-5 h-5" />Financial Rules</h2>
              <div className="bg-white border-2 border-gray-200 p-5 space-y-3">
                {["All outgoing payments require founder approval", "Critical decisions are blocked until approved", "Compliance veto is absolute and immediate", "No log = No execution (mandatory audit trail)"].map((r) => (
                  <div key={r} className="flex items-start gap-2"><Shield className="w-3 h-3 text-[#002FA7] mt-1 flex-shrink-0" /><p className="text-xs text-gray-600">{r}</p></div>
                ))}
              </div>
            </div>

            {/* Notifications */}
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}><AlertTriangle className="w-5 h-5" />Notifications ({notifications.length})</h2>
              <div className="bg-white border-2 border-gray-200">
                {notifications.length > 0 ? notifications.slice(0, 5).map((n, i) => (
                  <div key={n.id || `${n.agent}-${n.timestamp || i}`} className="p-3 border-b border-gray-200 last:border-0">
                    <p className="text-xs font-medium">{n.summary?.substring(0, 80)}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{n.agent} — {n.timestamp ? new Date(n.timestamp).toLocaleString() : ""}</p>
                  </div>
                )) : <div className="p-6 text-center text-gray-500 text-sm">Geen meldingen</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
